import { ILayer } from '../layer.type';
import { environment } from '../../../../../environments/environment';
import { getCSSColorVariableValue } from '../../../utils';
import { _ } from '../../../utils';
import { getSharedLayer } from './shared';
import { TenantConfig } from './tenant-config.type';
import { Expression, SymbolLayout, SymbolPaint } from 'mapbox-gl';

const markerUrlBase = environment.assetRoot + '/data/T13/markers/';

const pumpLayout: SymbolLayout = {
  'text-allow-overlap': true,
  'text-anchor': 'top',
  'text-radial-offset': 1,
  'text-size': 10,
};

const pumpPaint: SymbolPaint = {
  'text-color': 'black',
  'text-halo-color': 'white',
  'text-halo-width': 2,
};

const smallIconLayout: SymbolLayout = {
  'text-size': [
    'interpolate', ['linear'], ['zoom'],
    0, 3,
    13, 12,
    16, 15,
  ],
  'icon-size': [
    'interpolate', ['linear'], ['zoom'],
    0, 0.2,
    10, 0.4,
    16, 1.0,
  ],
};

const virtausLayout: SymbolLayout = {
  'icon-rotate': ['number', ['get', 'qnty'], 0],
  'text-field': ['get', 'ts'],
  'text-line-height': 0.95,
  'text-size': 11,
  'text-rotate': [
    // keep text upright (angle -90 to 90)
    'case',
    ['>', ['get', 'textAngle'], 90], ['-', ['get', 'textAngle'], 180],
    ['get', 'textAngle'],
  ],
  'text-anchor': [
    'case',
    ['>', ['get', 'textAngle'], 90], 'right',
    'left',
  ],
  'text-offset': ['get', 'textOffset'],
  'text-allow-overlap': true,
  'text-max-width': 15,
};

/**
 * Joensuu
 */
 export interface JoensuuTenantConfig extends TenantConfig {
  tenantCustomData: {
    urls: {
      api: string
    },
  };
}

export default (): JoensuuTenantConfig => ({
  layerDefinitions: getLayers(),
  layerDefinitionDefaults: {
    metadata: {
      visibleByZoom: 6,
    },
  },
  hasMultipleMarkerModes: false,
  defaultCenter: {
    lat: 62.598,
    lng: 29.783,
  },
  defaultZoom: 10.96,
  alarmFilters: [{
    id: 2,
    name: _('Priority'),
    type: 'dropdown-multiple',
    noFiltersShowsAll: true,
    filter_values: [
      {
        name: 'A-kriittiset',
        value: 1,
        boolean: true
      },
      {
        name: 'B-kriittiset',
        value: 2,
        boolean: true
      },
      {
        name: 'C-kriittiset',
        value: 3,
        boolean: true
      }
    ]
  }],
  tenantCustomData: {
    urls: {
      api: `${environment.apigwURL}jns-api/`
    }
  },
  vivoLookupPropertyId: '5ebccadba204d7041a884e6a',
  influxMeasurements: {
    bucket: 'miso-measurements',
    measurement: 'miso',
  },
  v2lMeasurements: {
    bucket: '',
    measurement: '',
  }
});

// vectorURL and vectorSourceLayer are filled in from encrypted-data/T13.json by MapService.addLayer
export function getLayers(): ILayer[] {
  return [
    {
      id: 'Näytteenottopisteet',
      name: _('Laboratory results'),
      belowTitles: false,
      title: 'Näytteenottopisteet',
      orderingWeight: -1,
      asMarkers: true,
      selectable: true,
      style: {
        icon: {
          url: markerUrlBase + 'Näytteenottopiste.png',
        },
        layerType: 'symbol',
        sourceType: 'geojson',
        visible: false,
      },
      metadata: {
        hoverComponent: 'V2LHover',
        uniqueBy: '_id',
        labelBy: '_id',
        v2lLayer: true,
        //// relate with network data ?
        //// NOTE: accuracy of selected feature's coordinates relates to zoom level
        visibleByZoom: 8,
      },
      searchOptions: {
        searchable: false
      },
      useWorker: true
    },
    {
      id: 'Kalusto',
      name: _('Equipment'),
      belowTitles: false,
      title: 'Kalusto',
      orderingWeight: -1,
      asMarkers: true,
      selectable: true,
      style: {
        icon: {
          url: markerUrlBase + 'Kalusto.png',
          rotation: [
            'case',
            ['==', ['get', 'speed'], 0], 0,
            ['+', ['get', 'course'], 0]
          ]
        },
        layerType: 'symbol',
        sourceType: 'geojson',
        visible: false,
      },
      metadata: {
        hoverComponent: 'FluentHover',
        uniqueBy: 'carid',
        labelBy: 'carid',
        flaggingDisabled: true
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 1,
        priority: 10,

        interestingProperties: [
          { key: 'cartype', type: 'contains' }
        ],
      },
      useWorker: true
    },
    {
      id: 'HP_havaintoputki',
      name: 'Havaintoputket',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'geojson',
        clickable: true,
        icon: {
          url: markerUrlBase + 'Havaintoputki.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: smallIconLayout,
      },
      metadata: {
        uniqueBy: 'Positiotunnus',
        labelBy: 'Positiotunnus',
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        measurementsFilters: {
          tunnus: 'Positiotunnus',
        },
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [{
          key: 'Positiotunnus',
          type: 'contains',
        }]
      },
      useWorker: true,
    },
    {
      id: 'JVL_jvpuhdistamo',
      name: 'Jätevedenpuhdistamot',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      linkable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'geojson',
        clickable: true,
        icon: {
          url: markerUrlBase + 'jätevesilaitos4.png',
        },
        visible: true,
      },
      metadata: {
        uniqueBy: 'Positiotunnus',
        labelBy: 'Nimi',
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        measurementsFilters: {
          tunnus: 'Positiotunnus',
        },
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [{
          key: 'Positiotunnus',
          type: 'startsWith',
        }, {
          key: 'Nimi',
          type: 'contains',
        }]
      },
      useWorker: true,
    },
    {
      id: 'JVP_jvpumppaamo',
      name: 'Jätevedenpumppaamot',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      linkable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'geojson',
        clickable: true,
        icon: {
          url: markerUrlBase + 'Pumppu.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...pumpLayout,
          ...smallIconLayout,
          'text-field': ['get', 'Positiotunnus'],
        },
        paint: pumpPaint,
      },
      metadata: {
        uniqueBy: 'Positiotunnus',
        labelBy: 'Positiotunnus',
        group: 'Pumppaamot',
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        measurementsFilters: {
          tunnus: 'Positiotunnus',
        },
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [{
          key: 'Positiotunnus',
          type: 'startsWith',
        }, {
          key: 'Nimi',
          type: 'startsWith',
        }]
      },
      useWorker: true,
    },
    {
      id: 'MA_mittausasema',
      name: 'Mittausasemat',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      linkable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'geojson',
        clickable: true,
        icon: {
          url: markerUrlBase + 'mittausasema.png',
        },
        visible: false,
      },
      metadata: {
        uniqueBy: 'Positiotunnus',
        // some features are missing the "Nimi" property
        labelBy: 'Positiotunnus',
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        measurementsFilters: {
          tunnus: 'Positiotunnus',
        },
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [{
          key: 'Positiotunnus',
          type: 'startsWith',
        }, {
          key: 'Nimi',
          type: 'contains',
        }]
      },
      mapboxOptions: {
        layout: smallIconLayout,
      },
      useWorker: true,
    },
    {
      id: 'VO_vedenottamo',
      name: 'Vedenottamot',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      linkable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'geojson',
        clickable: true,
        icon: {
          url: markerUrlBase + 'Vedenottamo.png',
        },
        visible: false,
      },
      metadata: {
        uniqueBy: 'Positiotunnus',
        // some features are missing the "Nimi" property
        labelBy: 'Positiotunnus',
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        measurementsFilters: {
          tunnus: 'Positiotunnus',
        },
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [{
          key: 'Positiotunnus',
          type: 'startsWith',
        }, {
          key: 'Nimi',
          type: 'contains',
        }]
      },
      mapboxOptions: {
        layout: smallIconLayout,
      },
      useWorker: true,
    },
    {
      id: 'VT_vesitorni',
      name: 'Vesitornit',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      linkable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'geojson',
        clickable: true,
        icon: {
          url: markerUrlBase + 'Vesitorni.png',
        },
        visible: true,
      },
      metadata: {
        uniqueBy: 'Positiotunnus',
        labelBy: 'Nimi',
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        measurementsFilters: {
          tunnus: 'Positiotunnus',
        },
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [{
          key: 'Positiotunnus',
          type: 'startsWith',
        }, {
          key: 'Nimi',
          type: 'contains',
        }]
      },
      useWorker: true,
    },
    {
      id: 'Virtaussuunta',
      name: _('Flow direction'),
      belowTitles: false,
      title: 'Virtaussuunta',
      orderingWeight: 999, //// layer items are not selectable
      asMarkers: true,
      selectable: false,
      style: {
        icon: {
          url: markerUrlBase + 'Virtaussuunta.png',
        },
        layerType: 'symbol',
        sourceType: 'geojson',
        visible: false,
      },
      mapboxOptions: {
        layout: virtausLayout
      },
      metadata: {
        uniqueBy: 'deviceId',
        labelBy: 'ts',
        roomName: 'suunta_virtaus' //// this should match for measurements iot (meta)data
      },
      geojsonPostprocess(data) {
        for (const feature of data.features) {
          //// text is by default in 90 degrees angle. flow arrow, by default, points to north (angle is zero)
          const textAngle = parseInt(feature.properties.qnty, 10) - 90;
          if (Number.isNaN(textAngle)) {
            continue;
          }
          feature.properties.textAngle = textAngle;

          // if overrotated, swap to the other side of the icon
          const offsetAmount = textAngle > 90 ? -3.5 : 3.5;
          // the angle is clamped to keep text upright
          const offsetAngle = textAngle > 90 ? (textAngle - 180) : textAngle;
          const textOffset = [
            Math.cos(offsetAngle * (Math.PI / 180)) * offsetAmount,
            Math.sin(offsetAngle * (Math.PI / 180)) * offsetAmount,
          ];
          feature.properties.textOffset = textOffset;
        }
        return data;
      },
      searchOptions: {
        searchable: false
      },
      useWorker: true
    },
    {
      id: 'Ilmakello',
      name: 'Ilmauskellot',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'geojson',
        clickable: true,
        icon: {
          url: markerUrlBase + 'Ilmakello.png',
        },
        visible: false,
      },
      metadata: {
        uniqueBy: 'Positiotunnus',
        // "Nimi" properties seem to the same value for all features, thus not very useful
        labelBy: 'Positiotunnus',
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        measurementsFilters: {
          tunnus: 'Positiotunnus',
        },
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [{
          key: 'Positiotunnus',
          type: 'startsWith',
        }, {
          key: 'Nimi',
          type: 'contains',
        }]
      },
      mapboxOptions: {
        layout: smallIconLayout,
      },
      useWorker: true,
    },
    {
      id: 'VL_vesilaitos',
      name: 'Vesilaitokset',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      linkable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'geojson',
        clickable: true,
        icon: {
          url: markerUrlBase + 'Vesilaitos2.png',
        },
        visible: true,
      },
      metadata: {
        uniqueBy: 'Positiotunnus',
        labelBy: 'Nimi',
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        measurementsFilters: {
          tunnus: 'Positiotunnus',
        },
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [{
          key: 'Positiotunnus',
          type: 'startsWith',
        }, {
          key: 'Nimi',
          type: 'contains',
        }]
      },
      useWorker: true,
    },
    {
      id: 'HVP_hulevesipumppaamo',
      name: 'Hulevesipumpaamot',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      linkable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'geojson',
        clickable: true,
        icon: {
          url: markerUrlBase + 'Pumppu.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...pumpLayout,
          ...smallIconLayout,
          'text-field': 'HVP',
        },
        paint: pumpPaint,
      },
      metadata: {
        uniqueBy: 'Positiotunnus',
        labelBy: 'Nimi',
        group: 'Pumppaamot',
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        measurementsFilters: {
          tunnus: 'Positiotunnus',
        },
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [{
          key: 'Positiotunnus',
          type: 'startsWith',
        }, {
          key: 'Nimi',
          type: 'contains',
        }]
      },
      useWorker: true,
    },
    {
      id: 'PK_paineenkorottamo',
      name: 'Paineenkorottamot',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      linkable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'geojson',
        clickable: true,
        icon: {
          url: markerUrlBase + 'Pumppu.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...pumpLayout,
          ...smallIconLayout,
          'text-field': 'PK',
        },
        paint: pumpPaint,
      },
      metadata: {
        uniqueBy: 'Positiotunnus',
        labelBy: 'Nimi',
        group: 'Pumppaamot',
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        measurementsFilters: {
          tunnus: 'Positiotunnus',
        },
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [{
          key: 'Positiotunnus',
          type: 'startsWith',
        }, {
          key: 'Nimi',
          type: 'contains',
        }]
      },
      useWorker: true,
    },
    {
      id: 'TA_tukiasema',
      name: 'Tukiasemat',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'geojson',
        clickable: true,
        icon: {
          url: markerUrlBase + 'Tukiasema.png',
        },
        visible: false,
      },
      metadata: {
        uniqueBy: 'Positiotunnus',
        labelBy: 'Nimi',
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        measurementsFilters: {
          tunnus: 'Positiotunnus',
        },
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [{
          key: 'Positiotunnus',
          type: 'startsWith',
        }, {
          key: 'Nimi',
          type: 'contains',
        }]
      },
      useWorker: true,
    },
    {
      id: 'PP_Paloposti',
      name: 'Palopostit',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'geojson',
        clickable: true,
        icon: {
          url: markerUrlBase + 'Paloposti.png',
        },
        visible: false,
      },
      metadata: {
        uniqueBy: 'Positiotunnus',
        labelBy: 'Nimi',
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        measurementsFilters: {
          tunnus: 'Positiotunnus',
        },
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [{
          key: 'Positiotunnus',
          type: 'startsWith',
        }, {
          key: 'Nimi',
          type: 'contains',
        }]
      },
      useWorker: true,
    },
    //
    // LINE DATA STARTS HERE
    //
    {
      id: 'JNS_wastewater_network',
      name: _('Wastewater network'),
      orderingWeight: 0,
      selectable: true,
      style: {
        clickable: true,
        strokeColor: '#cf923b',
        strokeWeight: 1.5,
        visible: true,
        layerType: 'line',
        sourceType: 'vector',
      },
      metadata: {
        visibleByZoom: 10,
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: { coordinates: true, properties: ['EntityHandle'] },
        uniqueBy: 'EntityHandle',
        labelBy: 'EntityHandle',
        group: 'Verkostot',
      },
      searchOptions: {
        interestingProperties: [{ key: 'EntityHandle', type: 'contains' }],
        searchable: false,
      },
      useWorker: true,
    },
    {
      id: 'JNS_clean_water_network',
      name: _('Clean water network'),
      orderingWeight: 0,
      selectable: true,
      style: {
        clickable: true,
        strokeColor: getCSSColorVariableValue('info-base'),
        strokeWeight: 1.5,
        visible: true,
        layerType: 'line',
        sourceType: 'vector',
      },
      metadata: {
        visibleByZoom: 10,
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: { coordinates: true, properties: ['EntityHandle'] },
        uniqueBy: 'EntityHandle',
        labelBy: 'EntityHandle',
        group: 'Verkostot',
      },
      searchOptions: {
        interestingProperties: [{ key: 'EntityHandle', type: 'contains' }],
        searchable: false,
      },
      useWorker: true,
    },
    {
      id: 'JNS_subsurface_drain_network',
      name: _('Subsurface drain network'),
      orderingWeight: 0,
      selectable: true,
      style: {
        clickable: true,
        strokeColor: getCSSColorVariableValue('success-base'),
        strokeWeight: 1.5,
        visible: false,
        layerType: 'line',
        sourceType: 'vector',
      },
      metadata: {
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: { coordinates: true, properties: ['EntityHandle'] },
        uniqueBy: 'EntityHandle',
        labelBy: 'EntityHandle',
        group: 'Verkostot',
      },
      searchOptions: {
        interestingProperties: [{ key: 'EntityHandle', type: 'contains' }],
        searchable: false,
      },
      useWorker: true,
    },
    {
      id: 'JNS_rainwater_network',
      name: _('Urban runoff network'),
      orderingWeight: 0,
      selectable: true,
      style: {
        clickable: true,
        strokeColor: getCSSColorVariableValue('success-base'),
        strokeWeight: 1.5,
        visible: false,
        layerType: 'line',
        sourceType: 'vector',
      },
      metadata: {
        visibleByZoom: 10,
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: { coordinates: true, properties: ['EntityHandle'] },
        uniqueBy: 'EntityHandle',
        labelBy: 'EntityHandle',
        group: 'Verkostot',
      },
      searchOptions: {
        interestingProperties: [{ key: 'EntityHandle', type: 'contains' }],
        searchable: false,
      },
      useWorker: true,
    },
    {
      id: 'VOK_clean_water_network',
      name: _('VOK clean water network'),
      orderingWeight: 0,
      selectable: true,
      style: {
        clickable: true,
        strokeColor: getCSSColorVariableValue('success-base'),
        strokeWeight: 1.5,
        visible: true,
        layerType: 'line',
        sourceType: 'vector',
      },
      metadata: {
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: { coordinates: true, properties: ['EntityHandle'] },
        uniqueBy: 'EntityHandle',
        labelBy: 'EntityHandle',
        group: 'Verkostot',
      },
      searchOptions: {
        interestingProperties: [{ key: 'EntityHandle', type: 'contains' }],
        searchable: false,
      },
      useWorker: true,
    },
    {
      id: 'VOK_wastewater_network',
      name: _('VOK wastewater network'),
      orderingWeight: 0,
      selectable: true,
      style: {
        clickable: true,
        strokeColor: getCSSColorVariableValue('success-base'),
        strokeWeight: 1.5,
        visible: true,
        layerType: 'line',
        sourceType: 'vector',
      },
      metadata: {
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: { coordinates: true, properties: ['EntityHandle'] },
        uniqueBy: 'EntityHandle',
        labelBy: 'EntityHandle',
        group: 'Verkostot',
      },
      searchOptions: {
        interestingProperties: [{ key: 'EntityHandle', type: 'contains' }],
        searchable: false,
      },
      useWorker: true,
    },
    getSharedLayer('houseNumbers'),
    getSharedLayer('elevationContour'),
    getSharedLayer('depthContour'),
    getSharedLayer('groundWaterAreas'),
    getSharedLayer('drainageAreas'),
    getSharedLayer('basinOutlets'),
  ];
}
